import React, { useState, useEffect, useRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { FiBell } from 'react-icons/fi';
import { NotificationsItem } from './notifications-item';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { EmptyState } from '@/components/custom/empty-state';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@/hooks/useNotification';

export const NotificationsWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notifications',
      // styles
      'relative inline-flex shrink-0 items-center gap-1',
      className
    )}
    {...props}
  />
));
NotificationsWrapper.displayName = 'NotificationsWrapper';

export const NotificationsToggle = React.forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'c-notifications-toggle',
      // styles
      'relative appearance-none border-none bg-none p-0 leading-[0] text-primary',
      '[&>svg]:w-4 [&>svg]:transition-transform [&>svg]:duration-200 hover:[&>svg]:scale-110',
      className
    )}
    {...props}
  />
));
NotificationsToggle.displayName = 'NotificationsToggle';

export const NotificationsBadge = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-notifications-badge',
      // styles
      'absolute right-0 top-0 block h-4 min-w-4 -translate-y-1/2 translate-x-1/2 rounded-full bg-[#FF4C6F] px-1 text-center text-[12px] font-bold leading-4 text-white',
      className
    )}
    {...props}
  />
));
NotificationsBadge.displayName = 'NotificationsBadge';

export const NotificationsContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notifications-content',
      // styles
      'absolute right-[-20px] top-[120%] z-[1] flex h-[calc(100vh-200px)] w-[680px] max-w-[80vw] animate-in flex-col overflow-hidden rounded-[10px] border border-solid border-primary-lightest bg-white shadow-[0px_40px_40px_0px_rgba(17,11,62,0.2)] duration-300 fade-in',
      className
    )}
    {...props}
  />
));
NotificationsContent.displayName = 'NotificationsContent';

export const NotificationsHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notifications-header',
      // styles
      'mx-[2vw] flex items-center justify-between gap-5 border-b border-solid border-primary-lightest pb-2 pt-9',
      className
    )}
    {...props}
  />
));
NotificationsHeader.displayName = 'NotificationsHeader';

export const NotificationsTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-notifications-title',
      // styles
      'mb-0 text-base font-bold leading-normal text-primary',
      className
    )}
    {...props}
  />
));
NotificationsTitle.displayName = 'NotificationsTitle';

export const NotificationsReadBtn = React.forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'c-notifications-read-btn',
      // styles
      'appearance-none border-none bg-transparent p-0 text-[12px] font-light text-neutral-darker outline-none',
      className
    )}
    {...props}
  />
));
NotificationsReadBtn.displayName = 'NotificationsReadBtn';

const notificationsBodyVariants = cva('scrollbar-none flex-grow overflow-x-hidden', {
  variants: {
    variant: {
      default: 'h-0 overflow-y-auto',
      dashboard: 'h-auto overflow-y-hidden rounded-[10px] border border-solid border-[#ECE9F8] shadow-dropshadow'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

export const NotificationsBody = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-notifications-body', notificationsBodyVariants({ variant }), className)}
    {...props}
  />
));
NotificationsBody.displayName = 'NotificationsBody';

export const NotificationItem = React.forwardRef(({ className, variant, read, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notification-item',
      // styles
      'flex cursor-pointer items-start gap-5 bg-white p-[2.5vw] transition-all duration-300 md:px-5 md:py-[1.2vw]',
      'border-b border-solid border-primary-lightest last-of-type:border-none',
      read && 'border-b border-white bg-bright',
      variant === 'center' && [
        'flex-wrap items-center gap-x-4 lg:flex-nowrap',
        // Seletor arbitrário para o Footer baseado na estrutura do SASS
        // Controla largura, alinhamento e texto conforme o breakpoint LG
        '[&_.c-notifications-item-footer]:w-full [&_.c-notifications-item-footer]:self-start [&_.c-notifications-item-footer]:text-right lg:[&_.c-notifications-item-footer]:w-auto lg:[&_.c-notifications-item-footer]:text-left'
      ],
      className
    )}
    {...props}
  />
));
NotificationItem.displayName = 'NotificationItem';

export const NotificationItemHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notification-item-header',
      // styles
      'shrink-0',
      className
    )}
    {...props}
  />
));
NotificationItemHeader.displayName = 'NotificationItemHeader';

export const NotificationItemIcon = React.forwardRef(({ className, variant, size, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notification-item-icon',
      // styles
      'relative flex h-10 w-10 items-center justify-center rounded-full bg-primary-lightest text-primary',
      variant === 'warning' && 'bg-warning-lightest text-warning',
      size === 'sm' && 'h-8 w-8',
      className
    )}
    {...props}
  />
));
NotificationItemIcon.displayName = 'NotificationItemIcon';

export const NotificationItemBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notification-item-body',
      // styles
      'w-0 flex-grow',
      className
    )}
    {...props}
  />
));
NotificationItemBody.displayName = 'NotificationItemBody';

export const NotificationItemTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn(
      'c-notification-item-title',
      // styles
      'mb-0 text-[12px] font-bold text-primary-dark',
      className
    )}
    {...props}
  />
));
NotificationItemTitle.displayName = 'NotificationItemTitle';

export const NotificationItemMore = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-notification-item-more',
      // styles
      'text-neutral-lighter',
      className
    )}
    {...props}
  />
));
NotificationItemMore.displayName = 'NotificationItemMore';

export const NotificationItemText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-notification-item-text',
      // styles
      'mb-0 line-clamp-2 overflow-hidden text-ellipsis text-[12px] font-light text-neutral',
      '[&_a]:font-bold [&_a]:text-primary [&_a]:underline',
      className
    )}
    {...props}
  />
));
NotificationItemText.displayName = 'NotificationItemText';

export const NotificationItemFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-notifications-item-footer',
      // styles
      'shrink-0',
      className
    )}
    {...props}
  />
));
NotificationItemFooter.displayName = 'NotificationItemFooter';

export const NotificationItemDateTime = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-notification-item-datetime',
      // styles
      'mb-0 line-clamp-2 overflow-hidden text-ellipsis text-[12px] font-light text-neutral',
      className
    )}
    {...props}
  />
));
NotificationItemDateTime.displayName = 'NotificationItemDateTime';

export function Notifications() {
  const { getNotificationsFeed, setNotificationsFeed, notificationsFeed, notificationFeedCount, setNotificationFeedCount } = useNotification();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const client = useClient();
  const menuRef = useRef();

  function handleReadBtn() {
    notificationsFeed.map(async n => {
      if (n.unread) {
        n.unread = false;
        const { error } = await client.mutate(['notifications', n.id], {
          unread: false
        });

        if (error) toast.error(t('toast.errorNotification'));
      }
    });
    setNotificationFeedCount(0);
  }

  useEffect(() => {
    const closeOnEscapeKeyDown = event => event.key === 'Escape' && isOpen && setIsOpen(false);
    const closeOnClickOutside = event => menuRef.current && !menuRef.current.contains(event.target) && setIsOpen(false);

    document.addEventListener('keydown', closeOnEscapeKeyDown);
    document.addEventListener('mouseup', closeOnClickOutside);

    getNotificationsFeed();

    return () => {
      document.removeEventListener('keydown', closeOnEscapeKeyDown);
      document.removeEventListener('mouseup', closeOnClickOutside);
    };
  }, [isOpen]);

  return (
    <NotificationsWrapper
      aria-label="Notificações"
      ref={menuRef}
    >
      <NotificationsToggle onClick={() => setIsOpen(!isOpen)}>
        <FiBell />
        {notificationFeedCount >= 1 && <NotificationsBadge>{notificationFeedCount}</NotificationsBadge>}
      </NotificationsToggle>

      {isOpen && (
        <NotificationsContent>
          <NotificationsHeader>
            <NotificationsTitle>{t('header.notificationTitle')}</NotificationsTitle>
            {notificationsFeed?.length > 0 && <NotificationsReadBtn onClick={handleReadBtn}>{t('header.notificationRead')}</NotificationsReadBtn>}
          </NotificationsHeader>

          <NotificationsBody>
            {notificationsFeed.length === 0 && (
              <EmptyState
                type="notification"
                title={t('emptyState.notificationTitle')}
                variant="plain"
              />
            )}

            {notificationsFeed.map((notification, index) => (
              <NotificationsItem
                key={index}
                notification={notification}
                notifications={notificationsFeed}
                setNotificationCount={setNotificationFeedCount}
                notificationCount={notificationFeedCount}
                setNotifications={setNotificationsFeed}
              />
            ))}
          </NotificationsBody>
        </NotificationsContent>
      )}
    </NotificationsWrapper>
  );
}
